import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle2, 
  CheckCircle,
  Mail, 
  Globe, 
  Sparkles, 
  ShieldCheck, 
  Copy, 
  Check, 
  Phone, 
  MapPin, 
  Clock, 
  Layers, 
  HelpCircle,
  Share2,
  FolderOpen,
  User,
  PenTool,
  Loader2,
  AlertTriangle,
  Server,
  CloudLightning,
  Key,
  X,
  CreditCard
} from 'lucide-react';
import { createWebsiteOnboarding } from '../lib/dbService';
import { TurnstileWidget, type TurnstileWidgetRef } from './TurnstileWidget';

interface WebsiteOnboardingGuideProps {
  onBack: () => void;
  onViewTerms?: () => void;
}

const TOTAL_STEPS = 5;

const AVAILABLE_FORM_FIELDS = [
  'Full Name',
  'Email Address',
  'Phone Number',
  'Message / Inquiry',
  'Company / Organization',
  'Service Requested',
  'Address / Location',
  'File / Attachment'
];

const SUBMISSION_VOLUME_OPTIONS = [
  'Up to 250 / mo',
  '250 - 1,000 / mo',
  '1,000 - 5,000 / mo',
  '5,000+ / mo'
];

export const WebsiteOnboardingGuide: React.FC<WebsiteOnboardingGuideProps> = ({ onBack, onViewTerms }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [leadOption, setLeadOption] = useState<'web3forms' | 'no-form'>('web3forms');
  const [formFields, setFormFields] = useState<string[]>([
    'Full Name',
    'Email Address',
    'Phone Number',
    'Message / Inquiry'
  ]);
  const [customFormFields, setCustomFormFields] = useState('');
  const [monthlySubmissions, setMonthlySubmissions] = useState<string>('Up to 250 / mo');

  const toggleFormField = (field: string) => {
    setFormFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };
  
  // Intake Form State - Primary Contact
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [clientSignature, setClientSignature] = useState('');
  const [signatureDate] = useState(() => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
  const [signatureError, setSignatureError] = useState(false);

  // Submission States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copiedBrief, setCopiedBrief] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  // Business & Website Details
  const [businessName, setBusinessName] = useState('');
  const [domainName, setDomainName] = useState('');
  const [domainStatus, setDomainStatus] = useState<'already_own' | 'need_purchase'>('already_own');
  const [hostingPreference, setHostingPreference] = useState<'randon_managed' | 'client_controlled'>('client_controlled');
  const [editingPreference, setEditingPreference] = useState<'hands_off' | 'self_managed'>('hands_off');
  const [tagline, setTagline] = useState('');
  const [address, setAddress] = useState('');
  const [hours, setHours] = useState('');
  const [socialLinks, setSocialLinks] = useState('');
  const [corePages, setCorePages] = useState('');
  const [brandColors, setBrandColors] = useState('');
  const [cloudFolderLink, setCloudFolderLink] = useState('');
  const [needsLogo, setNeedsLogo] = useState<'have_logo' | 'need_logo' | 'text_only'>('have_logo');
  const [logoNotes, setLogoNotes] = useState('');
  const [preferredLayout] = useState<'custom_randon' | string>('custom_randon');
  const [layoutNotes] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Close help modal on Escape key press
  useEffect(() => {
    if (!showHelpModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowHelpModal(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showHelpModal]);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('help@randonbrown.com');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2200);
  };

  const goToStep = (stepNumber: number) => {
    setCurrentStep(stepNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateStep1 = (): boolean => {
    if (!firstName.trim() || !lastName.trim() || !contactNumber.trim() || !email.trim()) {
      setSubmitError('Please complete all required fields (First Name, Last Name, Phone Number, and Email Address).');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSubmitError('Please enter a valid email address (e.g., name@example.com).');
      return false;
    }
    setSubmitError(null);
    return true;
  };

  const handleStepClick = (stepNumber: number) => {
    if (currentStep === 1 && stepNumber > 1) {
      if (!validateStep1()) return;
    }
    goToStep(stepNumber);
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }
    if (currentStep < TOTAL_STEPS) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  // Compile full intake summary for email
  const generateIntakeSummary = () => {
    const fullName = `${firstName} ${lastName}`.trim();
    const logoSummary = needsLogo === 'need_logo' 
      ? `Yes, Custom Logo Design Needed${logoNotes ? ` (Notes: ${logoNotes})` : ''}`
      : needsLogo === 'have_logo'
      ? 'Client has an existing logo (providing via email / cloud)'
      : 'No logo needed / simple typographic wordmark';

    return `=== WEBSITE ONBOARDING INTAKE ===
PRIMARY CONTACT:
Client Name: ${fullName || 'Not specified'}
Contact Number: ${contactNumber || 'Not specified'}
Email Address: ${email || 'Not specified'}
City / Zip: ${[city, zipCode].filter(Boolean).join(', ') || 'Not specified'}

BUSINESS & WEBSITE DETAILS:
Business Name: ${businessName || 'Not specified'}
Desired Domain: ${domainName || 'Not specified'} (${domainStatus === 'need_purchase' ? 'Needs to purchase domain (We recommend Cloudflare)' : 'Already owns domain elsewhere'})
Tagline / Slogan: ${tagline || 'None'}
Location / Hours: ${[address, hours].filter(Boolean).join(' | ') || 'Not specified'}
Logo Requirement: ${logoSummary}
Brand Colors / Styles: ${brandColors || 'None'}
Core Pages Needed: ${corePages || 'Home, About, Services, Contact'}
Social Media Links: ${socialLinks || 'None'}
Cloud Assets Link: ${cloudFolderLink || 'Files to be attached via direct email'}
Lead Delivery Preference: ${leadOption === 'no-form' ? "Direct Contact Only (No Form)" : `Contact Form on Website (Fields: ${(formFields.length > 0 ? formFields.join(', ') : 'Standard')} | Volume: ${monthlySubmissions} - Note: initial volume beyond 250 submissions requires a small upgrade fee to increase submission limits. Our forms are hosted on a third-party provider to ensure security, data compliance, and to ensure that you own your submission data.)`}
Site Editing Preference: ${editingPreference === 'self_managed' ? 'Self-Managed (If you need to edit your own site, additional charges apply based upon need)' : 'Complete Hands-Off (We take care of all website edits at our hourly rate)'}
Hosting Management Policy: To ensure that you retain ownership of your files, we require that your site is hosted on your hosting account and that we are added as a delegated administrator on your account to ensure security.

DATA BACKUPS POLICY:
We take your data backups very seriously. We carry multiple backups on various platforms to ensure your data is never lost. We also give you a copy upon completion.

TERMS & CONDITIONS ACKNOWLEDGEMENT:
Client acknowledges standard service policies: custom website builds start at $2,000, and updates or ongoing maintenance are billed on an agreed project or hourly basis. Client retains 100% direct ownership of their registrars, DNS, and hosting accounts. Balance is due upon project completion before live launch.

CLIENT AUTHORIZATION & SIGNATURE:
• Digital Signature: ${clientSignature}
• Date: ${signatureDate}`;
  };

  const handleCopyBrief = () => {
    navigator.clipboard.writeText(generateIntakeSummary());
    setCopiedBrief(true);
    setTimeout(() => setCopiedBrief(false), 2500);
  };

  const handleOpenEmailBackup = () => {
    const summary = generateIntakeSummary();
    const fullName = `${firstName} ${lastName}`.trim();
    const clientIdentifier = businessName || fullName || 'New Client';
    const subject = encodeURIComponent(`Website Onboarding Brief - ${clientIdentifier}`);
    const body = encodeURIComponent(`${summary}\n\n(Please attach any logo or photo files to this email if not linked above)`);
    window.location.href = `mailto:help@randonbrown.com?subject=${subject}&body=${body}`;
  };

  const handleFormSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !contactNumber.trim() || !email.trim()) {
      setSubmitError('Please complete all required fields (First Name, Last Name, Phone Number, and Email Address).');
      goToStep(1);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSubmitError('Please enter a valid email address (e.g., name@example.com).');
      goToStep(1);
      return;
    }

    if (!clientSignature.trim()) {
      setSignatureError(true);
      const sigElement = document.getElementById('onboarding-signature');
      if (sigElement) {
        sigElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!turnstileToken) {
      setSubmitError('Please complete the Cloudflare security verification below to proceed.');
      return;
    }

    setSignatureError(false);
    setIsSubmitting(true);
    setSubmitError(null);

    const result = await createWebsiteOnboarding({
      firstName,
      lastName,
      contactNumber,
      email,
      city,
      zipCode,
      businessName,
      domainName,
      domainStatus,
      hostingPreference,
      editingPreference,
      tagline,
      address,
      hours,
      socialLinks,
      corePages,
      brandColors,
      cloudFolderLink,
      needsLogo,
      logoNotes,
      preferredLayout,
      layoutNotes,
      leadOption,
      formFields: leadOption === 'no-form' ? [] : formFields,
      customFormFields: leadOption === 'no-form' ? '' : customFormFields,
      monthlySubmissions: leadOption === 'no-form' ? undefined : monthlySubmissions,
      selectedPackage: 'custom-build',
      githubUsername: '',
      stagedFileNames: [],
      clientSignature,
      signatureDate,
      turnstileToken: turnstileToken || undefined
    });

    setIsSubmitting(false);

    if (result.success && result.id) {
      setSubmissionId(result.id);
      setIsSubmitted(true);
    } else {
      setSubmitError(result.error || 'Failed to submit onboarding brief. Please try again or use the email backup.');
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  const stepTitles = [
    'Basic Info',
    'Lead Routing',
    'Domain Setup',
    'Hosting & Editing',
    'Timeline & Sign'
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-3 sm:px-6 py-4">
      {/* Hero Welcome Header */}
      <div className="text-center max-w-xl mx-auto space-y-1.5">
        <div className="inline-flex items-center gap-1.5 py-0.5 px-3 rounded-full bg-brand-500/10 text-brand-400 text-[11px] font-mono font-bold uppercase tracking-wider border border-brand-500/20">
          <Sparkles className="w-3 h-3" />
          Website Onboarding
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-black text-white tracking-tight">
          Client Onboarding Guide
        </h1>
        <p className="text-xs text-slate-400">
          Complete the quick steps below to configure your website preferences and launch plan.
        </p>
      </div>

      {/* Visual Step Progress Bar */}
      <div className="bg-neutral-950/80 border border-neutral-800/80 rounded-2xl p-2.5 sm:p-3 shadow-lg">
        <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
          {stepTitles.map((title, idx) => {
            const stepNum = idx + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;

            return (
              <button
                key={stepNum}
                onClick={() => handleStepClick(stepNum)}
                className={`flex flex-col items-center justify-center py-2 px-1 rounded-xl text-center transition-all cursor-pointer ${
                  isActive
                    ? 'bg-brand-500/15 border border-brand-500/40 text-brand-400 shadow-sm'
                    : isCompleted
                    ? 'bg-neutral-900/60 border border-emerald-500/30 text-emerald-400 hover:bg-neutral-900'
                    : 'bg-neutral-900/40 border border-neutral-900 text-slate-500 hover:text-slate-400'
                }`}
              >
                <div className="flex items-center gap-1">
                  {isCompleted ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <span className="text-[10px] font-mono font-bold">{stepNum}</span>
                  )}
                </div>
                <span className="text-[10px] sm:text-xs font-semibold truncate max-w-full hidden sm:inline mt-0.5">
                  {title}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic Step Content Container */}
      <div className="min-h-[420px]">
        <AnimatePresence mode="wait">

          {/* ==================== STEP 1 ==================== */}
          {currentStep === 1 && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="p-5 sm:p-7 rounded-3xl bg-neutral-950/90 border border-brand-500/20 shadow-xl space-y-5 text-left"
            >
              <div className="flex items-center justify-between border-b border-neutral-900 pb-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/30 flex items-center justify-center font-display font-black text-xs">
                    01
                  </span>
                  <div>
                    <h2 className="text-base sm:text-lg font-display font-bold text-white">
                      Basic Info &amp; Brand Assets
                    </h2>
                    <p className="text-[11px] text-slate-400 font-mono">Fill in your business and contact details</p>
                  </div>
                </div>
              </div>

              {/* Step 1 Validation Error Banner */}
              {submitError && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              {/* Cards Grid */}
              <div className="space-y-4">
                {/* Primary Contact Card */}
                <div className="p-4 sm:p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800 space-y-3">
                  <div className="flex items-center gap-2 text-white font-display font-bold text-xs sm:text-sm">
                    <User className="w-4 h-4 text-brand-400" />
                    <span>Primary Contact</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        <span className="text-brand-400">*</span> First Name
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="John"
                        value={firstName}
                        onChange={e => {
                          setFirstName(e.target.value);
                          if (submitError) setSubmitError(null);
                        }}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        <span className="text-brand-400">*</span> Last Name
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Smith"
                        value={lastName}
                        onChange={e => {
                          setLastName(e.target.value);
                          if (submitError) setSubmitError(null);
                        }}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                        <span className="text-brand-400">*</span> <Phone className="w-3 h-3 text-brand-400" /> Phone Number
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="(555) 000-0000"
                        value={contactNumber}
                        onChange={e => {
                          setContactNumber(e.target.value);
                          if (submitError) setSubmitError(null);
                        }}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                        <span className="text-brand-400">*</span> <Mail className="w-3 h-3 text-brand-400" /> Email Address
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="john@business.com"
                        value={email}
                        onChange={e => {
                          setEmail(e.target.value);
                          if (submitError) setSubmitError(null);
                        }}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-brand-400" /> City
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Houston, Rolla"
                        value={city}
                        onChange={e => setCity(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        placeholder="65483"
                        value={zipCode}
                        onChange={e => setZipCode(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Business & Domain Card */}
                <div className="p-4 sm:p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800 space-y-3">
                  <div className="flex items-center gap-2 text-white font-display font-bold text-xs sm:text-sm">
                    <Globe className="w-4 h-4 text-brand-400" />
                    <span>Business Details</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        <span className="text-brand-400">*</span> Official Business Name
                      </label>
                      <input
                        type="text"
                        placeholder="Acme Services LLC"
                        value={businessName}
                        onChange={e => setBusinessName(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                        <Globe className="w-3 h-3 text-brand-400" /> Desired Domain
                      </label>
                      <input
                        type="text"
                        placeholder="yourbusiness.com"
                        value={domainName}
                        onChange={e => setDomainName(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        Tagline or Slogan <span className="text-slate-500 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Quality service since 2010"
                        value={tagline}
                        onChange={e => setTagline(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        Physical Address or Service Area
                      </label>
                      <input
                        type="text"
                        placeholder="123 Main St or Dallas Metro Area"
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-brand-400" /> Operating Hours
                      </label>
                      <input
                        type="text"
                        placeholder="Mon - Fri: 8am - 5pm"
                        value={hours}
                        onChange={e => setHours(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo & Brand Style Card */}
                <div className="p-4 sm:p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800 space-y-3">
                  <div className="flex items-center gap-2 text-white font-display font-bold text-xs sm:text-sm">
                    <Sparkles className="w-4 h-4 text-brand-400" />
                    <span>Logo &amp; Brand Assets</span>
                  </div>

                  {/* 3-Option Logo Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div
                      onClick={() => setNeedsLogo('have_logo')}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        needsLogo === 'have_logo'
                          ? 'bg-brand-500/10 border-brand-500 text-white'
                          : 'bg-neutral-950 border-neutral-800 text-slate-300 hover:border-neutral-700'
                      }`}
                    >
                      <div className="text-left">
                        <div className="text-xs font-bold">Have Existing Logo</div>
                        <span className="text-[10px] text-slate-400">Sending files</span>
                      </div>
                      <CheckCircle2 className={`w-4 h-4 shrink-0 ${needsLogo === 'have_logo' ? 'text-brand-400' : 'text-slate-700'}`} />
                    </div>

                    <div
                      onClick={() => setNeedsLogo('need_logo')}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        needsLogo === 'need_logo'
                          ? 'bg-brand-500/10 border-brand-500 text-white'
                          : 'bg-neutral-950 border-neutral-800 text-slate-300 hover:border-neutral-700'
                      }`}
                    >
                      <div className="text-left">
                        <div className="text-xs font-bold">Need Logo Designed</div>
                        <span className="text-[10px] text-brand-400 font-semibold">Custom Design</span>
                      </div>
                      <Sparkles className={`w-4 h-4 shrink-0 ${needsLogo === 'need_logo' ? 'text-brand-400' : 'text-slate-700'}`} />
                    </div>

                    <div
                      onClick={() => setNeedsLogo('text_only')}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        needsLogo === 'text_only'
                          ? 'bg-brand-500/10 border-brand-500 text-white'
                          : 'bg-neutral-950 border-neutral-800 text-slate-300 hover:border-neutral-700'
                      }`}
                    >
                      <div className="text-left">
                        <div className="text-xs font-bold">Text Wordmark</div>
                        <span className="text-[10px] text-slate-400">Clean title text</span>
                      </div>
                      <Globe className={`w-4 h-4 shrink-0 ${needsLogo === 'text_only' ? 'text-brand-400' : 'text-slate-700'}`} />
                    </div>
                  </div>

                  {needsLogo === 'need_logo' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                      <input
                        type="text"
                        placeholder="Describe your logo idea (e.g., modern shield, tree icon, gold & black)"
                        value={logoNotes}
                        onChange={e => setLogoNotes(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs text-white placeholder-slate-600"
                      />
                    </motion.div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        Brand Colors / Style Notes
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Navy blue & gold, dark theme"
                        value={brandColors}
                        onChange={e => setBrandColors(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                        <Share2 className="w-3 h-3 text-brand-400" /> Google Drive / Dropbox Link
                      </label>
                      <input
                        type="url"
                        placeholder="Paste folder link (or email files later)"
                        value={cloudFolderLink}
                        onChange={e => setCloudFolderLink(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== STEP 2 ==================== */}
          {currentStep === 2 && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="p-5 sm:p-7 rounded-3xl bg-neutral-950/90 border border-brand-500/20 shadow-xl space-y-5 text-left"
            >
              <div className="flex items-center gap-2.5 border-b border-neutral-900 pb-3">
                <span className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/30 flex items-center justify-center font-display font-black text-xs">
                  02
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-display font-bold text-white">
                    Form &amp; Lead Delivery
                  </h2>
                  <p className="text-[11px] text-slate-400 font-mono">Choose how visitors get in touch with you</p>
                </div>
              </div>

              {/* 2 Clean Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Option 1: Web Form */}
                <div
                  onClick={() => setLeadOption('web3forms')}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                    leadOption === 'web3forms'
                      ? 'bg-brand-500/[0.08] border-brand-500/60 shadow-[0_0_20px_rgba(245,158,11,0.12)]'
                      : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                  }`}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                        leadOption === 'web3forms' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                      }`}>
                        {leadOption === 'web3forms' && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                    </div>
                    <h3 className="text-sm sm:text-base font-display font-bold text-white">Contact Form on Website</h3>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Visitor inquiries are sent directly to your email inbox with spam filtering and instant alerts.
                    </p>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-900 text-[11px] font-mono text-brand-400 font-medium">
                    Direct Email Delivery • Spam Protected
                  </div>
                </div>

                {/* Option 2: Direct Contact Only */}
                <div
                  onClick={() => setLeadOption('no-form')}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                    leadOption === 'no-form'
                      ? 'bg-brand-500/[0.08] border-brand-500/60 shadow-[0_0_20px_rgba(245,158,11,0.12)]'
                      : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                  }`}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-xl bg-neutral-900 text-slate-300 border border-neutral-800">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                        leadOption === 'no-form' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                      }`}>
                        {leadOption === 'no-form' && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                    </div>
                    <h3 className="text-sm sm:text-base font-display font-bold text-white">Direct Contact Only (No Form)</h3>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Clean click-to-call phone buttons and mailto links. No input forms on the website.
                    </p>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-900 text-[11px] font-mono text-slate-400 font-medium">
                    Click-to-call • Direct email link
                  </div>
                </div>
              </div>

              {/* Form Customization Options */}
              {leadOption !== 'no-form' && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 sm:p-5 rounded-2xl bg-neutral-900/60 border border-brand-500/20 space-y-4"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-mono font-bold text-white">
                        Form Fields Needed ({formFields.length} selected)
                      </label>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_FORM_FIELDS.map((field) => {
                        const isSelected = formFields.includes(field);
                        return (
                          <button
                            key={field}
                            type="button"
                            onClick={() => toggleFormField(field)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border flex items-center gap-1.5 cursor-pointer ${
                              isSelected
                                ? 'bg-brand-500/20 border-brand-400/60 text-white'
                                : 'bg-neutral-950 border-neutral-800 text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            <span className={`w-3 h-3 rounded border flex items-center justify-center text-[8px] shrink-0 ${
                              isSelected ? 'bg-brand-400 border-brand-400 text-neutral-950 font-bold' : 'border-neutral-700 bg-neutral-900'
                            }`}>
                              {isSelected ? '✓' : ''}
                            </span>
                            <span>{field}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Volume Selection */}
                  <div className="space-y-2 pt-2 border-t border-neutral-800">
                    <label className="text-xs font-mono font-bold text-white block">
                      Estimated Monthly Volume
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {SUBMISSION_VOLUME_OPTIONS.map((vol) => {
                        const isSelected = monthlySubmissions === vol;
                        return (
                          <button
                            key={vol}
                            type="button"
                            onClick={() => setMonthlySubmissions(vol)}
                            className={`p-2.5 rounded-xl text-xs font-medium transition-all border text-center cursor-pointer ${
                              isSelected
                                ? 'bg-brand-500/15 border-brand-400 text-white font-bold'
                                : 'bg-neutral-950 border-neutral-800 text-slate-300 hover:border-neutral-700'
                            }`}
                          >
                            {vol}
                          </button>
                        );
                      })}
                    </div>
                    <div className="p-3 rounded-xl bg-neutral-950 border border-brand-500/20 text-xs text-slate-300 leading-relaxed space-y-1 mt-2">
                      <p>
                        <strong className="text-brand-400 font-semibold">Volume Limits &amp; Security:</strong> Initial volume beyond 250 submissions requires a small upgrade fee to increase submission limits. Our forms are hosted on a third-party provider to ensure security, data compliance, and to ensure that you own your submission data.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ==================== STEP 3 ==================== */}
          {currentStep === 3 && (
            <motion.div
              key="step-3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="p-5 sm:p-7 rounded-3xl bg-neutral-950/90 border border-brand-500/20 shadow-xl space-y-5 text-left"
            >
              <div className="flex items-center gap-2.5 border-b border-neutral-900 pb-3">
                <span className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/30 flex items-center justify-center font-display font-black text-xs">
                  03
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-display font-bold text-white">
                    Domain Setup
                  </h2>
                  <p className="text-[11px] text-slate-400 font-mono">100% Client Ownership • Direct Account Control</p>
                </div>
              </div>

              {/* 2 Clean Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Option 1: Already Own */}
                <div
                  onClick={() => setDomainStatus('already_own')}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                    domainStatus === 'already_own'
                      ? 'bg-brand-500/[0.08] border-brand-500/60 shadow-[0_0_20px_rgba(245,158,11,0.12)]'
                      : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                  }`}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-xl bg-neutral-900 text-slate-300 border border-neutral-800">
                        <ShieldCheck className="w-4 h-4 text-brand-400" />
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                        domainStatus === 'already_own' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                      }`}>
                        {domainStatus === 'already_own' && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-display font-bold text-white">I Already Own My Domain</h3>
                      <span className="text-[10px] font-mono text-slate-400">GoDaddy, Namecheap, Google, etc.</span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Keep it where it is. We provide 2 simple DNS records to point to your new build.
                    </p>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-900 text-[11px] font-mono text-slate-400 font-medium">
                    No domain transfer required
                  </div>
                </div>

                {/* Option 2: Need to Purchase */}
                <div
                  onClick={() => setDomainStatus('need_purchase')}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                    domainStatus === 'need_purchase'
                      ? 'bg-brand-500/[0.08] border-brand-500/60 shadow-[0_0_20px_rgba(245,158,11,0.12)]'
                      : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                  }`}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                        <Globe className="w-4 h-4" />
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                        domainStatus === 'need_purchase' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                      }`}>
                        {domainStatus === 'need_purchase' && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-display font-bold text-white">I Need to Purchase a Domain</h3>
                      <span className="text-[10px] font-mono text-slate-400 block mt-0.5">Direct Client Account Control</span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      You maintain 100% direct ownership of your domain registrar account.
                    </p>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-900 text-[11px] font-mono text-brand-400 font-medium">
                    We recommend Cloudflare.
                  </div>
                </div>
              </div>

              {/* Status Note */}
              <div className="p-3.5 rounded-2xl bg-neutral-900/50 border border-neutral-800 text-xs text-slate-300 flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>
                  {domainStatus === 'already_own'
                    ? 'Simple DNS linking provided during initial setup—no transfer fees.'
                    : 'We will guide you through purchasing directly under your own personal or company account. We recommend Cloudflare.'}
                </span>
              </div>
            </motion.div>
          )}

          {/* ==================== STEP 4: HOSTING & EDITING ==================== */}
          {currentStep === 4 && (
            <motion.div
              key="step-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="p-5 sm:p-7 rounded-3xl bg-neutral-950/90 border border-brand-500/20 shadow-xl space-y-6 text-left"
            >
              <div className="flex items-center gap-2.5 border-b border-neutral-900 pb-3">
                <span className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/30 flex items-center justify-center font-display font-black text-xs">
                  04
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-display font-bold text-white">
                    Hosting, Website Editing &amp; Backups
                  </h2>
                  <p className="text-[11px] text-slate-400 font-mono">Select your management, editing, and deployment preferences</p>
                </div>
              </div>

              {/* Section 1: Website Editing Preference */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-brand-400" />
                  <h3 className="text-xs sm:text-sm font-display font-bold text-white uppercase tracking-wider font-mono">
                    1. Website Editing Preference
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Hands-Off */}
                  <div
                    onClick={() => setEditingPreference('hands_off')}
                    className={`p-4 sm:p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                      editingPreference === 'hands_off'
                        ? 'bg-brand-500/[0.08] border-brand-500/60 shadow-[0_0_20px_rgba(245,158,11,0.12)]'
                        : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold uppercase">
                          Managed Edits
                        </span>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                          editingPreference === 'hands_off' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                        }`}>
                          {editingPreference === 'hands_off' && <Check className="w-3 h-3 stroke-[3]" />}
                        </div>
                      </div>
                      <h4 className="text-sm font-bold text-white">Complete Hands-Off Editing</h4>
                      <p className="text-xs text-slate-300 leading-relaxed font-semibold">
                        We take care of all website edits at our hourly rate.
                      </p>
                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        Enjoy peace of mind with zero CMS maintenance headaches—just email or call whenever you need updates.
                      </p>
                    </div>
                    <div className="mt-3 pt-2 border-t border-neutral-900 text-[11px] font-mono text-brand-400 font-medium">
                      We take care of all website edits at our hourly rate.
                    </div>
                  </div>

                  {/* Self-Managed */}
                  <div
                    onClick={() => setEditingPreference('self_managed')}
                    className={`p-4 sm:p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                      editingPreference === 'self_managed'
                        ? 'bg-brand-500/[0.08] border-brand-500/60 shadow-[0_0_20px_rgba(245,158,11,0.12)]'
                        : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 font-bold uppercase">
                          Self-Managed CMS
                        </span>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                          editingPreference === 'self_managed' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                        }`}>
                          {editingPreference === 'self_managed' && <Check className="w-3 h-3 stroke-[3]" />}
                        </div>
                      </div>
                      <h4 className="text-sm font-bold text-white">I Prefer to Edit My Own Site</h4>
                      <p className="text-xs text-slate-300 leading-relaxed font-semibold">
                        If you need to edit your own site, additional charges apply based upon need.
                      </p>
                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        An administrative dashboard is configured for independent editing, with custom training or support provided as needed.
                      </p>
                    </div>
                    <div className="mt-3 pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 font-medium">
                      If you need to edit your own site, additional charges apply based upon need.
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Hosting Management */}
              <div className="space-y-3 pt-3 border-t border-neutral-900">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-brand-400" />
                  <h3 className="text-xs sm:text-sm font-display font-bold text-white uppercase tracking-wider font-mono">
                    2. Hosting Management
                  </h3>
                </div>

                {/* Mandated Hosting Management & Account Ownership Policy Card */}
                <div className="p-5 rounded-2xl bg-neutral-900/80 border border-brand-500/30 text-xs text-slate-200 leading-relaxed space-y-2 shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-bold text-white font-display text-sm">
                      <ShieldCheck className="w-4.5 h-4.5 text-brand-400 shrink-0" />
                      <span>Hosting Management &amp; Account Ownership Policy</span>
                    </div>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20 font-bold uppercase">
                      Mandatory Policy
                    </span>
                  </div>
                  <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                    To ensure that you retain ownership of your files, we require that your site is hosted on your hosting account and that we are added as a delegated administrator on your account to ensure security.
                  </p>
                </div>
              </div>

              {/* Section 3: Backups Section */}
              <div className="space-y-3 pt-3 border-t border-neutral-900">
                <div className="flex items-center gap-2">
                  <CloudLightning className="w-4 h-4 text-brand-400" />
                  <h3 className="text-xs sm:text-sm font-display font-bold text-white uppercase tracking-wider font-mono">
                    3. Data Backups
                  </h3>
                </div>

                <div className="p-4 sm:p-5 rounded-2xl bg-neutral-900/80 border border-brand-500/30 text-left space-y-2.5 shadow-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white font-display flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-400" />
                      Data Backup Guarantee
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">
                      Multi-Platform
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-200 leading-relaxed font-semibold">
                    We take your data backups very seriously. We carry multiple backups on various platforms to ensure your data is never lost. We also give you a copy upon completion.
                  </p>
                  <p className="text-xs text-slate-400 leading-relaxed pt-1 border-t border-neutral-800">
                    Your complete website code, database dumps, and media archives are mirrored across secure storage environments, and a full standalone archive copy is handed directly to you upon project sign-off.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* ==================== STEP 5 ==================== */}
          {currentStep === 5 && (
            <motion.div
              key="step-5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="space-y-5 text-left"
            >
              {isSubmitted ? (
                <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-emerald-500/30 text-center space-y-5 shadow-2xl">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>

                  <div className="space-y-1.5">
                    <span className="text-[11px] font-mono uppercase tracking-wider px-3 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">
                      Received &amp; Logged
                    </span>
                    <h2 className="text-xl sm:text-2xl font-display font-bold text-white">
                      Website Project Brief Received!
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-300 max-w-md mx-auto">
                      Thank you, <span className="text-white font-semibold">{firstName} {lastName}</span>! Your project intake details and digital signature have been securely recorded.
                    </p>
                  </div>

                  {/* Summary Box */}
                  <div className="p-4 rounded-2xl bg-neutral-900/70 border border-neutral-800 text-left max-w-md mx-auto space-y-2.5 text-xs">
                    <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                      <span className="font-mono text-slate-400">Brief Ref ID</span>
                      <span className="font-mono font-bold text-brand-400">
                        #{submissionId ? submissionId.slice(0, 8).toUpperCase() : 'WEB-DIRECT'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <span className="text-slate-500 block font-mono uppercase">Business</span>
                        <span className="text-white font-medium">{businessName || 'New Client'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block font-mono uppercase">Domain</span>
                        <span className="text-white font-medium">{domainName || 'To Be Acquired'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block font-mono uppercase">Contact</span>
                        <span className="text-white font-medium">{contactNumber || email}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block font-mono uppercase">Signature</span>
                        <span className="text-brand-300 italic">{clientSignature}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto pt-1">
                    <button
                      onClick={onBack}
                      className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider cursor-pointer transition-all active:scale-95"
                    >
                      Return to Websites
                    </button>
                    <button
                      onClick={handleCopyBrief}
                      className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold cursor-pointer transition-all flex items-center justify-center gap-1.5"
                    >
                      {copiedBrief ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-emerald-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 text-brand-400" />
                          <span>Copy Brief</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-5 sm:p-7 rounded-3xl bg-neutral-950/90 border border-brand-500/20 shadow-xl space-y-5 text-left">
                  <div className="flex items-center gap-2.5 border-b border-neutral-900 pb-3">
                    <span className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/30 flex items-center justify-center font-display font-black text-xs">
                      05
                    </span>
                    <div>
                      <h2 className="text-base sm:text-lg font-display font-bold text-white">
                        Timeline &amp; Sign-Off
                      </h2>
                      <p className="text-[11px] text-slate-400 font-mono">Review brief, project milestones &amp; authorize launch</p>
                    </div>
                  </div>

                  {/* 3 Milestone Badges */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-800 space-y-1">
                      <span className="text-[10px] font-mono text-brand-400 font-bold uppercase">1. Start</span>
                      <h4 className="text-xs font-bold text-white">Intake &amp; 50% Deposit</h4>
                      <p className="text-[11px] text-slate-400">Review assets &amp; setup staging</p>
                    </div>

                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-800 space-y-1">
                      <span className="text-[10px] font-mono text-brand-400 font-bold uppercase">2. Build</span>
                      <h4 className="text-xs font-bold text-white">Staging Preview</h4>
                      <p className="text-[11px] text-slate-400">Review live site draft &amp; revisions</p>
                    </div>

                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-800 space-y-1">
                      <span className="text-[10px] font-mono text-brand-400 font-bold uppercase">3. Launch</span>
                      <h4 className="text-xs font-bold text-white">Balance &amp; Live Go-Live</h4>
                      <p className="text-[11px] text-slate-400">Point DNS &amp; handover archive</p>
                    </div>
                  </div>

                  {/* Compact Intake Summary Review */}
                  <div className="p-4 rounded-2xl bg-neutral-900/60 border border-neutral-800 space-y-2.5">
                    <div className="flex items-center justify-between border-b border-neutral-800/80 pb-2">
                      <span className="text-xs font-bold text-white font-display flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                        Summary Review
                      </span>
                      <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                        Auto-Synced
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      <div className="p-2.5 rounded-xl bg-neutral-950 border border-neutral-800/80">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block">Business</span>
                        <span className="font-semibold text-white truncate block">{businessName || 'Not specified'}</span>
                      </div>

                      <div className="p-2.5 rounded-xl bg-neutral-950 border border-neutral-800/80">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block">Domain</span>
                        <span className="font-semibold text-brand-400 truncate block">{domainName || 'To be purchased'}</span>
                      </div>

                      <div className="p-2.5 rounded-xl bg-neutral-950 border border-neutral-800/80">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block">Contact</span>
                        <span className="font-semibold text-white truncate block">{firstName || lastName ? `${firstName} ${lastName}`.trim() : (contactNumber || email || 'Not specified')}</span>
                      </div>

                      <div className="p-2.5 rounded-xl bg-neutral-950 border border-neutral-800/80">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block">Editing Style</span>
                        <span className="font-semibold text-amber-400 text-xs block">
                          {editingPreference === 'self_managed' ? 'Self-Managed CMS' : 'Complete Hands-Off'}
                        </span>
                      </div>

                      <div className="p-2.5 rounded-xl bg-neutral-950 border border-neutral-800/80">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block">Hosting</span>
                        <span className="font-semibold text-emerald-400 text-xs block">
                          Client Account (Delegated Admin)
                        </span>
                      </div>

                      <div className="p-2.5 rounded-xl bg-neutral-950 border border-neutral-800/80">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block">Lead Routing</span>
                        <span className="font-semibold text-cyan-400 text-xs block">
                          {leadOption === 'no-form' ? 'Direct Contact' : 'Contact Form'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Digital Signature Card */}
                  <div 
                    id="onboarding-signature" 
                    className={`p-4 sm:p-5 rounded-2xl bg-neutral-900/80 border ${
                      signatureError 
                        ? 'border-red-500/80 shadow-[0_0_20px_rgba(239,68,68,0.25)]' 
                        : 'border-brand-500/30'
                    } space-y-3 text-left transition-all`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <PenTool className="w-4 h-4 text-brand-400" />
                        <h3 className="text-xs sm:text-sm font-bold text-white font-display">
                          Digital Signature &amp; Authorization
                        </h3>
                      </div>
                      <span className="text-[11px] font-mono text-slate-400">
                        {signatureDate}
                      </span>
                    </div>

                    <div>
                      <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                        <span className="text-brand-400">*</span> Type Full Name to Authorize Project Start
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g., John M. Smith"
                        value={clientSignature}
                        onChange={e => {
                          setClientSignature(e.target.value);
                          if (e.target.value.trim()) setSignatureError(false);
                        }}
                        className={`w-full px-3.5 py-2.5 rounded-xl bg-neutral-950 border ${
                          signatureError ? 'border-red-500' : 'border-neutral-800 focus:border-brand-500'
                        } focus:outline-none text-white placeholder-slate-500 font-serif italic text-base sm:text-lg`}
                      />
                      {signatureError && (
                        <p className="text-xs font-medium text-red-400 pt-1">
                          Please enter your signature before submitting.
                        </p>
                      )}
                    </div>

                    <div className="text-[11px] text-slate-400 flex items-center justify-between gap-2 pt-1 border-t border-neutral-800">
                      <span>Custom websites start at $2,000. Balance is due upon final approval and completion prior to live launch.</span>
                      {onViewTerms && (
                        <button
                          type="button"
                          onClick={onViewTerms}
                          className="text-brand-400 hover:text-brand-300 underline shrink-0 font-semibold cursor-pointer"
                        >
                          Terms &rarr;
                        </button>
                      )}
                    </div>

                    {/* Cloudflare Turnstile Human Verification Challenge */}
                    <div className="pt-2">
                      <TurnstileWidget
                        ref={turnstileRef}
                        siteKey="0x4AAAAAAFLhILYKdp64I0Y2"
                        action="contact"
                        onSuccess={(token) => {
                          setTurnstileToken(token);
                          setSubmitError(null);
                        }}
                        onError={() => {
                          setTurnstileToken(null);
                        }}
                        onExpire={() => {
                          setTurnstileToken(null);
                        }}
                      />
                    </div>
                  </div>

                  {/* Submit Button Section */}
                  {submitError && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                        <span>{submitError}</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleOpenEmailBackup}
                        className="underline text-red-200 hover:text-white font-medium cursor-pointer shrink-0"
                      >
                        Email Instead
                      </button>
                    </div>
                  )}

                  <div className="pt-1">
                    <button
                      onClick={handleFormSubmit}
                      disabled={isSubmitting || !turnstileToken}
                      className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-brand-500 to-amber-500 hover:from-brand-400 hover:to-amber-400 text-neutral-950 font-extrabold text-xs uppercase tracking-wider font-mono flex items-center justify-center gap-2 shadow-lg shadow-brand-500/20 active:scale-95 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Submitting Brief...</span>
                        </>
                      ) : !turnstileToken ? (
                        <>
                          <ShieldCheck className="w-4 h-4 opacity-70" />
                          <span>Verify Security Check to Submit</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Submit Project Brief</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Bottom Step Navigation Controls */}
      <div className="flex items-center justify-between pt-3 border-t border-neutral-900">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-xs sm:text-sm font-semibold transition-all ${
            currentStep === 1
              ? 'opacity-40 border-neutral-900 text-slate-600 cursor-not-allowed'
              : 'bg-neutral-900 hover:bg-neutral-800 border-neutral-800 text-slate-300 hover:text-white cursor-pointer active:scale-95'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Previous</span>
        </button>

        <span className="text-xs font-mono text-slate-500">
          Step {currentStep} of {TOTAL_STEPS}
        </span>

        {currentStep < TOTAL_STEPS ? (
          <button
            onClick={nextStep}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs sm:text-sm font-bold uppercase tracking-wider font-mono shadow-md shadow-brand-500/20 active:scale-95 transition-all cursor-pointer"
          >
            <span>Next Step</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs sm:text-sm font-semibold transition-all cursor-pointer"
          >
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Finish</span>
          </button>
        )}
      </div>

      {/* Help Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showHelpModal && (
            <div 
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto"
              onClick={() => setShowHelpModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md p-6 rounded-3xl bg-neutral-950 border border-brand-500/30 shadow-2xl space-y-4 text-left my-auto"
              >
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-neutral-900 transition-colors cursor-pointer"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-brand-500/10 text-brand-400 rounded-xl border border-brand-500/20">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white font-display">Need Help?</h3>
                    <p className="text-xs text-brand-400 font-mono">Dedicated Client Support</p>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-slate-300 leading-relaxed border-t border-b border-neutral-900 py-3">
                  <p>
                    Have questions or want us to walk you through account setup? Email{' '}
                    <a href="mailto:help@randonbrown.com" className="text-brand-400 font-semibold underline">
                      help@randonbrown.com
                  </a>.
                  </p>
                  <p className="text-slate-400">
                    We'll gladly hop on a quick 5-minute screen-share to help with DNS or assets.
                  </p>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <button
                    onClick={handleCopyEmail}
                    className="px-3 py-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 text-slate-300 hover:text-white text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    {copiedEmail ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedEmail ? 'Copied' : 'Copy Email'}</span>
                  </button>

                  <a
                    href="mailto:help@randonbrown.com?subject=Website%20Onboarding%20Assistance"
                    className="px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold flex items-center gap-1.5 transition-all shadow-md active:scale-95 cursor-pointer"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span>Email Us</span>
                  </a>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default WebsiteOnboardingGuide;
